/**
 * src/app/RequireAuth.tsx
 *
 * Route-guard wrapper for Phase 16 login-wall (ACCESS-01, ACCESS-05).
 *
 * Render order (CR-01 fix — prevents PaywallPage flash for trial/entitled users):
 *   1. authLoading → null (session not yet resolved)
 *   2. !userId    → LandingPage (anonymous)
 *   3. userId && !planLoaded → null (authenticated; wait for plan fetch to settle)
 *   4. access==='none' && !readOnlyPreview → PaywallPage (plan resolved; truly expired)
 *   5. children (trial, entitled, or readOnlyPreview soft-lock)
 *
 * Without step 3, the store default access:'none' caused every authenticated user to
 * briefly see PaywallPage on every page load (~100–500ms) until fetchAndSyncPlan resolved.
 *
 * WR-04: When readOnlyPreview is true (user clicked "View projects (read-only)"),
 * children are rendered even with access==='none'. The app body shows a sticky
 * ReadOnlyBanner with an upgrade CTA. Server requireAccess (Phase 15) still returns
 * 402 on writes — the soft-lock is honest and server-enforced (T-16-04).
 *
 * Otherwise renders LandingPage (anonymous) or PaywallPage (expired) IN PLACE —
 * the URL is NEVER changed (no useNavigate / Navigate). This preserves deep-link
 * URLs for post-login routing.
 *
 * CRITICAL: RequireAuth is NOT an authorization boundary.
 * Server-side `requireAccess` middleware (Phase 15) is the real gate.
 * This is UX only — it hides the editor UI from non-entitled users (T-16-04).
 *
 * Migration timing (T-16-05): AuthInit mounts before <Routes> inside AppProvider,
 * so migrateLocalToCloud fires before this component renders any page.
 * Do NOT move AuthInit below Routes or gate it behind this guard.
 */

import { FC, ReactNode } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import LandingPage from '@/pages/LandingPage';
import PaywallPage from '@/pages/PaywallPage';

interface RequireAuthProps {
  children: ReactNode;
}

export const RequireAuth: FC<RequireAuthProps> = ({ children }) => {
  const { authLoading, userId, planLoaded, access, readOnlyPreview } = useAuthStore();

  // Step 1: AUTH-05 pattern (identical to SessionWidget isPending guard):
  // Return null during session hydration — prevents flash of wrong screen.
  // authLoading defaults to true, is set to false by setUser/setAuthResolved/clearUser.
  // Body bg-color (bg-slate-50/950) is applied via Tailwind base styles so no white flash.
  if (authLoading) return null;

  // Step 2: Anonymous user: render LandingPage IN PLACE. URL is unchanged (no redirect).
  // Post-login: <Routes> will render the correct page at the same URL.
  if (!userId) return <LandingPage />;

  // Step 3 (CR-01): Authenticated but plan not yet fetched — return null to avoid the
  // brief PaywallPage flash caused by the store default access:'none'. planLoaded flips true
  // when fetchAndSyncPlan settles (success: setAccess sets it; failure: setPlanLoaded sets it).
  // Body bg-color matches the app background so there is no visible blank during this wait.
  if (!planLoaded) return null;

  // Step 4: Plan resolved; expired — unless the user has activated soft-lock preview (WR-04).
  // readOnlyPreview lets the user view their projects read-only; writes return 402 server-side.
  if (access === 'none' && !readOnlyPreview) return <PaywallPage />;

  // Step 5: Trial, entitled, or soft-lock read-only preview: render the real app routes.
  // ReadOnlyBanner mounts in App.tsx when readOnlyPreview is true (sticky upgrade CTA).
  return <>{children}</>;
};
